import { XMarkIcon } from '@heroicons/react/24/outline';
import type { ReactNode } from 'react';

import { cn } from '@/utils/classname';

export type CalloutVariant = 'info' | 'error' | 'warning';

const variantStyles: Record<
  CalloutVariant,
  { container: string; icon: string; dismiss: string }
> = {
  info: {
    container: 'border-chariot-stroke-info bg-chariot-status-info/10',
    icon: 'text-chariot-status-info',
    dismiss: 'text-chariot-status-info hover:bg-chariot-status-info/20',
  },
  error: {
    container: 'border-chariot-stroke-critical bg-chariot-stroke-critical/10',
    icon: 'text-chariot-stroke-critical',
    dismiss: 'text-chariot-stroke-critical hover:bg-chariot-stroke-critical/20',
  },
  warning: {
    container: 'border-chariot-stroke-high bg-chariot-stroke-high/10',
    icon: 'text-chariot-stroke-high',
    dismiss: 'text-chariot-stroke-high hover:bg-chariot-stroke-high/20',
  },
};

interface InfoCalloutProps {
  /** Visual variant controlling border/background/icon colors. */
  variant?: CalloutVariant;
  /** Icon rendered to the left of the content area. */
  icon?: ReactNode;
  /** Main content — text, links, headings, etc. */
  children: ReactNode;
  /** When provided, a dismiss (×) button is rendered on the right. */
  onDismiss?: () => void;
  /** Accessible label for the dismiss button (required when onDismiss is set). */
  dismissLabel?: string;
  /** Optional ARIA role for the outer container (e.g. "region"). */
  role?: string;
  /** Optional aria-label for the outer container. */
  'aria-label'?: string;
  /** Optional aria-live for the outer container. */
  'aria-live'?: 'off' | 'polite' | 'assertive';
  /** Optional test id. */
  'data-testid'?: string;
  /** Extra classes merged onto the outer container. */
  className?: string;
}

export function InfoCallout({
  variant = 'info',
  icon,
  children,
  onDismiss,
  dismissLabel = 'Dismiss',
  role: roleProp,
  className,
  ...rest
}: InfoCalloutProps) {
  const styles = variantStyles[variant];

  return (
    <div
      role={roleProp}
      aria-label={rest['aria-label']}
      aria-live={rest['aria-live']}
      data-testid={rest['data-testid']}
      className={cn(
        'flex items-start gap-3 rounded-lg border px-4 py-3',
        styles.container,
        className
      )}
    >
      {icon && (
        <span className={cn('mt-0.5 shrink-0', styles.icon)}>{icon}</span>
      )}

      <div className="text-chariot-text-primary min-w-0 flex-1 text-sm">
        {children}
      </div>

      {onDismiss && (
        <button
          type="button"
          className={cn('shrink-0 rounded p-1', styles.dismiss)}
          onClick={onDismiss}
          aria-label={dismissLabel}
        >
          <XMarkIcon className="size-4" />
        </button>
      )}
    </div>
  );
}
